import {
  TableRow,
  TableCell,
  Typography,
  Box,
  Stack,
  Chip,
  Avatar,
  Tooltip,
} from '@mui/material'
import {
  Email as EmailIcon,
  Sms as SmsIcon,
  Warning as WarningIcon,
} from '@mui/icons-material'
import { Client } from '../../services/api'
import {
  ClientDeliverableChips,
  getDaysInStage,
  AGING_DAYS,
  needsTeamAssignment,
} from './clientStatusChips'

interface ClientPipelineRowProps {
  client: Client
  /** Label for the client's current stage, even when that stage is inactive. */
  currentStageLabel: string
  /** True when the client sits in a stage that is no longer configured. */
  stageIsUnconfigured: boolean
  onClientClick: (client: Client) => void
  showAssignee: boolean
}

export default function ClientPipelineRow({
  client,
  currentStageLabel,
  stageIsUnconfigured,
  onClientClick,
  showAssignee,
}: ClientPipelineRowProps) {
  const daysInStage = getDaysInStage(client.updatedAt)
  const isAging = daysInStage > AGING_DAYS
  const needsTeam = needsTeamAssignment(client)
  const unreadEmail = client.unreadEmailCount ?? 0
  const unreadSms = client.unreadSmsCount ?? 0
  const networkingCount = client.networkingContactCount ?? 0
  const lowNetworking = client.hasLinkedinImport && networkingCount < 5
  const incompleteTasks = client.incompleteTaskCount ?? 0

  return (
    <TableRow hover>
      <TableCell sx={{ maxWidth: 260 }}>
        <Box
          component="button"
          type="button"
          onClick={() => onClientClick(client)}
          sx={{
            border: 0,
            background: 'none',
            p: 0,
            m: 0,
            font: 'inherit',
            color: 'inherit',
            textAlign: 'left',
            cursor: 'pointer',
            display: 'block',
            width: '100%',
            '&:hover .client-name': { textDecoration: 'underline' },
          }}
        >
          <Typography variant="body2" component="span" className="client-name" sx={{ fontWeight: 600, display: 'block' }}>
            {client.name}
          </Typography>
          <Typography
            variant="caption"
            component="span"
            color="text.secondary"
            sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            {client.email}
          </Typography>
        </Box>
      </TableCell>

      {/* Stage is a readout here. The table exists to show everyone at once;
          moving a client through the pipeline is the board's job, and two
          controls for one transition is how they drift apart. */}
      <TableCell sx={{ minWidth: 190 }}>
        <Typography variant="body2">{currentStageLabel}</Typography>
        {stageIsUnconfigured && (
          <Typography variant="caption" color="text.secondary">
            not configured
          </Typography>
        )}
      </TableCell>

      <TableCell sx={{ minWidth: 170 }}>
        <Stack direction="row" alignItems="center" spacing={0.75} useFlexGap sx={{ flexWrap: 'wrap' }}>
          {needsTeam && (
            <Tooltip title="This client needs a team assignment before work can begin">
              <Chip
                label="Assign team"
                size="small"
                color="error"
                variant="outlined"
              />
            </Tooltip>
          )}
          {client.clientSuccess && (
            <Tooltip title={`Client Success: ${client.clientSuccess}`}>
              <Avatar sx={{ width: 22, height: 22, fontSize: '0.625rem', bgcolor: 'success.main' }}>
                CS
              </Avatar>
            </Tooltip>
          )}
          {showAssignee && client.assignedTo && (
            <>
              <Tooltip title={`Owner: ${client.assignedTo}`}>
                <Avatar sx={{ width: 22, height: 22, fontSize: '0.625rem', bgcolor: 'primary.main' }}>
                  {client.assignedTo.charAt(0).toUpperCase()}
                </Avatar>
              </Tooltip>
              <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 90 }}>
                {client.assignedTo.split('@')[0]}
              </Typography>
            </>
          )}
        </Stack>
      </TableCell>

      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
        <Typography
          variant="body2"
          sx={{ color: isAging ? 'warning.dark' : 'text.secondary', fontWeight: isAging ? 600 : 400 }}
        >
          {isAging && (
            <WarningIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'text-bottom' }} />
          )}
          {daysInStage} {daysInStage === 1 ? 'day' : 'days'}
        </Typography>
      </TableCell>

      <TableCell sx={{ minWidth: 220 }}>
        <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: 'wrap' }}>
          <ClientDeliverableChips client={client} />
        </Stack>
      </TableCell>

      <TableCell sx={{ minWidth: 180 }}>
        <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: 'wrap' }}>
          {unreadEmail > 0 && (
            <Tooltip
              title={`${unreadEmail} unread email${unreadEmail === 1 ? '' : 's'} — open the Emails thread to clear`}
            >
              <Chip
                icon={<EmailIcon sx={{ fontSize: 14 }} />}
                label={unreadEmail}
                size="small"
                color="info"
              />
            </Tooltip>
          )}
          {unreadSms > 0 && (
            <Tooltip
              title={`${unreadSms} unread SMS — open the Messages conversation to clear`}
            >
              <Chip
                icon={<SmsIcon sx={{ fontSize: 14 }} />}
                label={unreadSms}
                size="small"
                color="info"
              />
            </Tooltip>
          )}
          {lowNetworking && (
            <Tooltip
              title={`Low network coverage — ${networkingCount} connection${networkingCount === 1 ? '' : 's'} imported`}
            >
              <Chip
                icon={<WarningIcon sx={{ fontSize: 14 }} />}
                label={`${networkingCount} contact${networkingCount === 1 ? '' : 's'}`}
                size="small"
                color="warning"
                variant="outlined"
              />
            </Tooltip>
          )}
          {incompleteTasks > 0 && (
            <Tooltip title="Gate tasks must be completed before this client can advance">
              <Chip
                icon={<WarningIcon sx={{ fontSize: 14 }} />}
                label={`${incompleteTasks} gate task${incompleteTasks === 1 ? '' : 's'}`}
                size="small"
                color="warning"
                variant="outlined"
              />
            </Tooltip>
          )}
          {unreadEmail === 0 && unreadSms === 0 && !lowNetworking && incompleteTasks === 0 && (
            <Typography variant="body2" color="text.secondary">
              —
            </Typography>
          )}
        </Stack>
      </TableCell>

    </TableRow>
  )
}
